import PushNotificationLifecycle from './components/PushNotificationLifecycle';
import MobileShell from './mobile/MobileShell';
import AuditActivityTracker from './components/AuditActivityTracker';
import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";
import "./components/erp.css";
import { MotionConfig } from "framer-motion";
import AppBoundary from "./components/AppBoundary";
import LoadingState from "./components/LoadingState";
import NotFound from "./components/NotFound";
// src/App.js

// Page Imports
const Login = lazy(() => import("./pages/Login"));
const PrivacyAccount = lazy(() => import('./pages/PrivacyAccount'));
const PrivacyRequests = lazy(() => import('./pages/PrivacyRequests'));
const PrivacyNotice = lazy(() => import('./pages/PrivacyNotice'));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Issues = lazy(() => import("./pages/Issues"));
const Reports = lazy(() => import("./pages/Reports"));
const StudentData = lazy(() => import("./pages/StudentData"));
const ManageUsers = lazy(() => import("./pages/ManageUsers"));
const AuditTrail = lazy(() => import("./pages/AuditTrail"));
const Attendance = lazy(() => import("./pages/Attendance"));
const UserAddition = lazy(() => import("./pages/UserAddition"));
const FacultyTiming = lazy(() => import("./pages/FacultyTiming"));
const FacultySchedule = lazy(() => import("./pages/FacultySchedule")); 
const ScheduleBuilder = lazy(() => import("./pages/ScheduleBuilder")); 
const StudentIssueForm = lazy(() => import("./pages/StudentIssueForm"));
const StudentTicketTracking = lazy(() => import("./pages/StudentTicketTracking"));
const StudentComplaintPortal = lazy(() => import("./pages/StudentComplaintPortal"));
const IssueAllocatorDashboard = lazy(() => import("./pages/IssueAllocatorDashboard")); 
const MyTasksDashboard = lazy(() => import("./pages/MyTasksDashboard")); 
const StudentIRForm = lazy(() => import("./pages/StudentIRForm"));
const IRManager = lazy(() => import("./pages/IRManager"));
const StudentFeedbackBuilder = lazy(() => import("./pages/StudentFeedbackBuilder"));
const StudentFeedbackForm = lazy(() => import("./pages/StudentFeedbackForm"));
const ExamResultsManager = lazy(() => import("./pages/ExamResultsManager"));
const TaskSettings = lazy(() => import("./pages/TaskSettings")); 
const ResultsManager = lazy(() => import("./pages/ResultsManager")); 
const ResultPosters = lazy(() => import('./pages/ResultPosters'));
const StudentResultForm = lazy(() => import("./pages/StudentResultForm"));
const StudentResultUpload = lazy(() => import("./pages/StudentResultUpload")); 
const PRPManager = lazy(() => import("./pages/PRPManager")); 
const SalesManager = lazy(() => import("./pages/SalesManager")); 
const SalesFormBuilder = lazy(() => import("./pages/SalesFormBuilder")); 
const SalesForm = lazy(() => import("./pages/SalesForm")); 
const AbsenceReason = lazy(() => import("./pages/AbsenceReason")); // <-- NEW: Absence Reason Form

// --- NEW HR MODULE IMPORTS ---
const EmployeeOnboarding = lazy(() => import("./pages/EmployeeOnboarding"));
const HRManager = lazy(() => import("./pages/HRManager"));
const EmployeePortal = lazy(() => import("./pages/EmployeePortal"));

// --- NEW FLOOR PLAN OPS IMPORT ---
const FloorPlanOps = lazy(() => import("./pages/FloorPlanOps"));

// --- NEW FINANCE MODULE IMPORTS ---
const FinanceDashboard = lazy(() => import("./pages/FinanceDashboard"));
const DirectorFinanceDashboard = lazy(() => import("./pages/DirectorFinanceDashboard"));
const BudgetPlanning = lazy(() => import('./pages/BudgetPlanning'));
const DirectorSettings = lazy(() => import("./pages/DirectorSettings"));

// --- NEW MARKETING TRAVEL MODULE IMPORTS ---
const MarketingTravelModule = lazy(() => import("./pages/MarketingTravelModule"));
const ReimbursementDesk = lazy(() => import("./pages/ReimbursementDesk"));


function App() {

  return (
    <MotionConfig reducedMotion="user"><AppBoundary><AuthProvider>
      <Router>
        <MobileShell />
        <PushNotificationLifecycle />
        <AuditActivityTracker />
        <Suspense fallback={<LoadingState />}><Routes>
          
          {/* --- PUBLIC ROUTES (No Login Required) --- */}
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyNotice />} />
          <Route path="/privacy-account" element={<ProtectedRoute><PrivacyAccount /></ProtectedRoute>} />
          <Route path="/privacy-requests" element={<ProtectedRoute requiredFeature="accessHR"><PrivacyRequests /></ProtectedRoute>} />
          
          {/* Students use these to submit/track issues */}
          <Route path="/submit-ticket" element={<StudentIssueForm />} />
          <Route path="/track-complaints" element={<StudentComplaintPortal />} />
          <Route path="/student/ticket/:id" element={<StudentTicketTracking />} />
          
          {/* Public IR Registration Form for Students */}
          <Route path="/ir-registration" element={<StudentIRForm />} />

          {/* Public Student Feedback Form (Takes ID from URL) */}
          <Route path="/feedback/:id" element={<StudentFeedbackForm />} />

          {/* Public Result Photo Upload for Students */}
          <Route path="/result-form/:token" element={<StudentResultForm />} />
          <Route path="/student-upload/:id" element={<StudentResultUpload />} />

          {/* Public Ad Campaign Form for External Leads */}
          <Route path="/sales-form/:id" element={<SalesForm />} /> 

          {/* Public HR Onboarding Portal */}
          <Route path="/onboarding/:token" element={<EmployeeOnboarding />} />

          {/* Public route for students to submit absence reasons */}
          <Route path="/absence-reason" element={<AbsenceReason />} />

          {/* --- PROTECTED ROUTES (Login Required) --- */}

          <Route 
            path="/" 
            element={
              <ProtectedRoute requiredFeature="accessDailyOps">
                <Dashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/floor-plan-ops" 
            element={
              <ProtectedRoute requiredFeature="accessFloorPlan">
                <FloorPlanOps />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/attendance" 
            element={
              <ProtectedRoute requiredFeature="accessAttendance">
                <Attendance />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/user-addition" 
            element={
              <ProtectedRoute requiredFeature="accessUserAddition">
                <UserAddition />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/faculty-timing" 
            element={
              <ProtectedRoute requiredFeature="accessFacultyTiming">
                <FacultyTiming />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/schedule" 
            element={
              <ProtectedRoute requiredFeature="accessSchedule">
                <FacultySchedule />
              </ProtectedRoute>
            } 
          />

          {/* --- NEW SCHEDULE BUILDER --- */}
          <Route 
            path="/schedule-builder" 
            element={
              <ProtectedRoute requiredFeature="accessSchedule">
                <ScheduleBuilder />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/issues" 
            element={
              <ProtectedRoute>
                <Issues />
              </ProtectedRoute>
            } 
          />

          {/* --- NEW TICKET ALLOCATION DESK --- */}
          <Route 
            path="/allocate-tickets" 
            element={
              <ProtectedRoute>
                <IssueAllocatorDashboard />
              </ProtectedRoute>
            } 
          />

          {/* --- NEW MY TASKS DASHBOARD --- */}
          <Route 
            path="/my-tasks" 
            element={
              <ProtectedRoute>
                <MyTasksDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/ir-manager" 
            element={
              <ProtectedRoute requiredFeature="accessIR">
                <IRManager />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/exams" 
            element={
              <ProtectedRoute requiredFeature="accessExams">
                <ExamResultsManager />
              </ProtectedRoute>
            } 
          />

          {/* Results Service (Calling/Data Entry) */}
          <Route 
            path="/results-service" 
            element={
              <ProtectedRoute requiredFeature="accessResultsService">
                <ResultsManager />
              </ProtectedRoute>
            } 
          />

          <Route path="/result-posters" element={<ProtectedRoute requiredFeature="accessResultPosters"><ResultPosters /></ProtectedRoute>} />

          {/* Sales Service (Lead Calling/Data Entry) */}
          <Route 
            path="/sales-service" 
            element={
              <ProtectedRoute requiredFeature="accessSales">
                <SalesManager />
              </ProtectedRoute>
            } 
          />

          {/* Sales Ad Form Builder (List View) */}
          <Route 
            path="/sales-form-builder" 
            element={
              <ProtectedRoute requiredFeature="accessSales">
                <SalesFormBuilder />
              </ProtectedRoute>
            } 
          />

          {/* Sales Ad Form Builder (Edit View) */}
          <Route 
            path="/sales-form-builder/:id" 
            element={
              <ProtectedRoute requiredFeature="accessSales">
                <SalesFormBuilder />
              </ProtectedRoute>
            } 
          />

          {/* PRP Manager (Performance Pay) */}
          <Route 
            path="/prp-manager" 
            element={
              <ProtectedRoute requiredFeature="accessPRP">
                <PRPManager />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/feedback-builder" 
            element={
              <ProtectedRoute requiredFeature="accessFeedbackBuilder">
                <StudentFeedbackBuilder />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/feedback-builder/:id" 
            element={
              <ProtectedRoute requiredFeature="accessFeedbackBuilder">
                <StudentFeedbackBuilder />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/reports" 
            element={
              <ProtectedRoute requiredFeature="accessReports">
                <Reports />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/students" 
            element={
              <ProtectedRoute requiredFeature="accessStudents">
                <StudentData />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/users" 
            element={
              <ProtectedRoute requiredFeature="accessUserManagement">
                <ManageUsers />
              </ProtectedRoute>
            } 
          />

          <Route path="/audit-trail" element={<ProtectedRoute requiredFeature="accessAuditTrail"><AuditTrail /></ProtectedRoute>} />

          <Route 
            path="/settings/tasks" 
            element={
              <ProtectedRoute requiredFeature="accessUserManagement">
                <TaskSettings />
              </ProtectedRoute>
            } 
          />

          {/* --- NEW HR MODULE ROUTES --- */}
          
          {/* Admin HR Dashboard */}
          <Route 
            path="/hr-dashboard" 
            element={
              <ProtectedRoute requiredFeature="accessHR">
                <HRManager />
              </ProtectedRoute>
            } 
          />

          {/* Employee Self-Service Portal */}
          <Route 
            path="/my-portal" 
            element={
              <ProtectedRoute requiredFeature="accessEmployeePortal">
                <EmployeePortal />
              </ProtectedRoute>
            } 
          />

          {/* --- NEW FINANCE MODULE ROUTES --- */}
          
          <Route 
            path="/finance" 
            element={
              <ProtectedRoute requiredFeature="accessFinance">
                <FinanceDashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/finance-config" 
            element={
              <ProtectedRoute requiredFeature="accessFinanceConfig">
                <DirectorFinanceDashboard />
              </ProtectedRoute>
            } 
          />

          <Route path="/finance-budgets" element={
            <ProtectedRoute requiredFeature="accessFinance" allowedRoles={['director', 'accountant']}>
              <BudgetPlanning />
            </ProtectedRoute>
          } />

          {/* --- NEW DIRECTOR SETTINGS ROUTE FOR SIGNATURES --- */}
          <Route 
            path="/director-settings" 
            element={
              <ProtectedRoute requiredFeature="directorOnly">
                <DirectorSettings />
              </ProtectedRoute>
            } 
          />

          {/* --- NEW MARKETING TRAVEL ROUTES --- */}
          <Route 
            path="/marketing-travel" 
            element={
              <ProtectedRoute requiredFeature="accessSales">
                <MarketingTravelModule />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/finance-reimbursements" 
            element={
              <ProtectedRoute requiredFeature="accessFinance">
                <ReimbursementDesk />
              </ProtectedRoute>
            } 
          />

        <Route path="*" element={<NotFound />} /></Routes></Suspense>
      </Router>
    </AuthProvider></AppBoundary></MotionConfig>
  );
}

export default App;
